import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { format, parseISO } from 'date-fns'
import {
  DateInput,
  Header,
  Loadable,
  NavFilter,
  Pagination,
  TableHead,
  navigateTo,
  useApiGet,
  useModal,
  useSortableTable,
  varClass,
} from '../shared'
import {
  IAnomaliesV2Alert,
  IAnomaliesV2AlertsResponse,
  IAnomaliesV2DatesResponse,
  IAnomaliesV2Summary,
  IAnomaliesV2TimelineResponse,
  TAnomaliesSuppressedMode,
  TAnomaliesSuppressionScope,
} from '../shared/types'
import { AdvertiserSearch } from './AdvertiserSearch'
import { Faq } from './Faq'
import {
  SUPPRESSION_METRIC_LABELS,
  SuppressModal,
  SuppressScopeChoice,
} from './SuppressModal'
import { PresetsControl, findDefaultPreset, useUserFilters } from './presets'
import { UserFilterValues } from './api'
import { TimelineChart } from './charts/TimelineChart'
import {
  FAMILY_COLORS,
  FAMILY_LABELS,
  FilterGroup,
  NetworkScope,
  NetworkScopePill,
  PAGE_SIZE_OPTIONS,
  applyNetworkScopeParams,
  badgesFor,
  displayLabelFor,
  europeanizeNumbers,
  formatDecimal,
  formatDkkLarge,
  formatInteger,
  formatMetricValue,
  formatPercentChange,
  formatScore,
  isCommissionMissing,
  useAnomaliesNetworks,
} from './shared'

const FAMILY_OPTIONS: Record<string, string> = {
  '': 'All families',
  drift: 'Drift',
  acute: 'Acute',
}

const METRIC_OPTIONS: Record<string, string> = {
  '': 'All metrics',
  cr: 'CR',
  epc: 'EPC',
  spc: 'SPC',
  clicks: 'Clicks',
  conversions: 'Conversions',
}

const STATUS_OPTIONS: Record<string, string> = {
  '': 'All statuses',
  new: 'New',
  worsening: 'Worsening',
  continuing: 'Continuing',
}

const DEVICE_OPTIONS: Record<string, string> = {
  '': 'All devices',
  Mobile: 'Mobile',
  Desktop: 'Desktop',
}

// All triage columns sort client-side. NEW outranks WORSENING outranks
// CONTINUING when sorting by status; impact uses commission as a stable proxy.
const STATUS_RANK = (a: IAnomaliesV2Alert): number => {
  if (a.isFirstAlertDay) return 0
  if (a.isWorseningToday) return 1
  return 2
}

const sortValueFor = (
  alert: IAnomaliesV2Alert,
  field: string | null | undefined
): number | string => {
  switch (field) {
    case 'rank':
      return alert.rank ?? Infinity
    case 'status':
      return STATUS_RANK(alert)
    case 'advertiser_domain':
      return (alert.advertiserDomain || '').toLowerCase()
    case 'device':
      return alert.device || ''
    case 'issue':
      return alert.alertFamily + '|' + alert.primaryMetric
    case 'current_value':
      return alert.currentValue ?? -Infinity
    case 'baseline_value':
      return alert.baselineValue ?? -Infinity
    case 'percent_change':
      return alert.percentChange ?? -Infinity
    case 'impact':
      return alert.commission30dDkk ?? -Infinity
    case 'priority_score':
      return alert.priorityScore ?? -Infinity
    case 'alert_first_seen_date':
      return alert.alertFirstSeenDate || ''
    case 'alert_days_active':
      return alert.alertDaysActive ?? -Infinity
    default:
      return alert.priorityScore ?? -Infinity
  }
}

// Snapshot of triage state persisted across navigation to the detail page so
// "Back to triage" lands on the same row, scroll, filters, etc. Stored in
// sessionStorage so it survives a single browser session but doesn't leak
// across tabs or browser restarts.
const TRIAGE_STATE_KEY = 'anomalies-triage-state-v1'

interface TriageSnapshot {
  dateString?: string
  search?: string
  searchInput?: string
  filters?: { key: string; label: string; value: string }[]
  sort?: string
  dir?: 'asc' | 'desc'
  page?: number
  pageSize?: number
  timelineDays?: number
  networkScope?: NetworkScope
  customNetworkUuids?: string[]
  scrollY?: number
}

const readTriageSnapshot = (): TriageSnapshot => {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(TRIAGE_STATE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const writeTriageSnapshot = (patch: TriageSnapshot) => {
  if (typeof window === 'undefined') return
  try {
    const existing = readTriageSnapshot()
    window.sessionStorage.setItem(
      TRIAGE_STATE_KEY,
      JSON.stringify({ ...existing, ...patch })
    )
  } catch {
    /* ignore quota / private mode failures */
  }
}

export const TriageView = () => {
  const dates = useApiGet<IAnomaliesV2DatesResponse>('/anomalies/v2/dates')

  // Read once at mount so all initial state values can come from the snapshot.
  const initialSnap = useMemo<TriageSnapshot>(() => readTriageSnapshot(), [])

  // Was there a sessionStorage snapshot when the tab booted? Drives the
  // first-visit auto-apply of the default preset — if the operator was already
  // mid-task we restore their state rather than steamrolling it with their
  // default.
  const hadSnapshotOnMount = useMemo(() => {
    if (typeof window === 'undefined') return false
    try {
      return !!window.sessionStorage.getItem(TRIAGE_STATE_KEY)
    } catch {
      return false
    }
  }, [])

  const [date, setDate] = useState<Date | null>(
    initialSnap.dateString ? parseISO(initialSnap.dateString) : null
  )
  // Pull the latest available date from /dates the first time it loads — but
  // only if the snapshot didn't already restore one.
  useEffect(() => {
    if (dates && dates.latestDate && !date) {
      setDate(parseISO(dates.latestDate))
    }
  }, [dates])

  const dateString = date ? moment(date).format('YYYY-MM-DD') : ''

  // Suppression view mode. "exclude" (default) hides suppressed advertisers;
  // "only" shows just them. We intentionally don't persist this in the
  // snapshot — re-visiting triage should land back in the clean default view.
  const [suppressedMode, setSuppressedMode] = useState<TAnomaliesSuppressedMode>('exclude')

  // Bumped after any suppress/unsuppress action so the three suppression-aware
  // queries refetch in lockstep.
  const [suppressionRefetchKey, setSuppressionRefetchKey] = useState(0)

  // Network scope pill (All / Direct / Affiliate / Custom). Heylink Inbound's
  // UUID is env-specific; we look it up via the v2 networks endpoint. The
  // full networks list is needed for the Custom picker + the smart shorter-
  // list decision in applyNetworkScopeParams.
  const [networkScope, setNetworkScope] = useState<NetworkScope>(
    initialSnap.networkScope ?? 'all'
  )
  const [customNetworkUuids, setCustomNetworkUuids] = useState<string[]>(
    initialSnap.customNetworkUuids ?? []
  )
  const { networks, heylinkUuid } = useAnomaliesNetworks()

  const [timelineDays, setTimelineDays] = useState<number>(
    initialSnap.timelineDays ?? 90
  )
  const timelineParams = useMemo(() => {
    if (!dateString) return undefined
    const end = moment(date!)
    const start = end.clone().subtract(timelineDays, 'days')
    const p: Record<string, string | number> = {
      start_date: start.format('YYYY-MM-DD'),
      end_date: end.format('YYYY-MM-DD'),
      suppressed: suppressedMode,
    }
    return applyNetworkScopeParams(p, networkScope, heylinkUuid, customNetworkUuids, networks)
  }, [dateString, timelineDays, suppressedMode, networkScope, heylinkUuid, customNetworkUuids, networks])
  const timeline = useApiGet<IAnomaliesV2TimelineResponse>(
    '/anomalies/v2/timeline',
    timelineParams,
    [dateString, timelineDays, suppressedMode, networkScope, heylinkUuid, JSON.stringify(customNetworkUuids), suppressionRefetchKey]
  )

  const [search, setSearch] = useState(initialSnap.search ?? '')
  const [searchInput, setSearchInput] = useState(
    initialSnap.searchInput ?? initialSnap.search ?? ''
  )
  const [filters, setFilters] = useState(
    initialSnap.filters ?? [
      { key: 'device', label: '', value: '' },
      { key: 'alert_family', label: '', value: '' },
      { key: 'primary_metric', label: '', value: '' },
      { key: 'status', label: '', value: '' },
    ]
  )

  const filterValue = (key: string) =>
    filters.find((f) => f.key === key)?.value || ''
  const device = filterValue('device')
  const alertFamily = filterValue('alert_family')
  const primaryMetric = filterValue('primary_metric')
  const status = filterValue('status')

  const { sort, dir, toggleSort } = useSortableTable({
    sort: initialSnap.sort ?? 'priority_score',
    dir: initialSnap.dir ?? 'desc',
  })

  const [page, setPage] = useState(initialSnap.page ?? 1)
  const [pageSize, setPageSize] = useState(initialSnap.pageSize ?? 10)

  // Optimistic hide: when the operator suppresses a row from the list, we
  // remove it from the visible alerts immediately, then let the refetch
  // settle the truth. Keys are `domain` (all-alerts suppression) or
  // `domain|metric` (metric-scoped) so a scoped suppression only hides its
  // own rows. 'rejections'-scoped suppressions never touch daily triage.
  const [optimisticHidden, setOptimisticHidden] = useState<Set<string>>(
    () => new Set()
  )

  const isOptimisticallyHidden = (a: IAnomaliesV2Alert): boolean =>
    optimisticHidden.has(a.advertiserDomain) ||
    optimisticHidden.has(`${a.advertiserDomain}|${a.primaryMetric}`)

  const { showModal, hideModal } = useModal()

  const triggerSuppressionRefetch = () => {
    setSuppressionRefetchKey((k) => k + 1)
  }

  const openSuppressModal = (
    advertiserDomain: string,
    initialScope: SuppressScopeChoice = ''
  ) => {
    showModal(
      <SuppressModal
        advertiserDomain={advertiserDomain}
        initialScope={initialScope}
        hideModal={hideModal}
        onSuppressed={(domain, scope) => {
          if (scope !== 'rejections') {
            setOptimisticHidden((prev) => {
              const next = new Set(prev)
              next.add(scope ? `${domain}|${scope}` : domain)
              return next
            })
          }
          triggerSuppressionRefetch()
        }}
      />
    )
  }

  // Clear optimistic hides whenever the next fetch settles — the backend
  // result becomes the source of truth from that point.
  useEffect(() => {
    setOptimisticHidden(new Set())
  }, [suppressionRefetchKey, suppressedMode, dateString])

  // Reset to page 1 on any filter/sort change. Skip the very first run so the
  // page number restored from the snapshot survives mount.
  const skipPageReset = React.useRef(true)
  useEffect(() => {
    if (skipPageReset.current) {
      skipPageReset.current = false
      return
    }
    setPage(1)
  }, [dateString, search, device, alertFamily, primaryMetric, status, sort, dir, pageSize, networkScope, customNetworkUuids])

  // Persist state changes to sessionStorage so back-nav from detail restores.
  useEffect(() => {
    writeTriageSnapshot({
      dateString,
      search,
      searchInput,
      filters,
      sort,
      dir: dir as 'asc' | 'desc',
      page,
      pageSize,
      timelineDays,
      networkScope,
      customNetworkUuids,
    })
  }, [
    dateString,
    search,
    searchInput,
    filters,
    sort,
    dir,
    page,
    pageSize,
    timelineDays,
    networkScope,
    customNetworkUuids,
  ])

  const captureScrollBeforeNav = () => {
    if (typeof window === 'undefined') return
    writeTriageSnapshot({ scrollY: window.scrollY })
  }

  const summaryParams = useMemo(() => {
    if (!dateString) return undefined
    const p: Record<string, string | number> = {
      date: dateString,
      suppressed: suppressedMode,
    }
    return applyNetworkScopeParams(p, networkScope, heylinkUuid, customNetworkUuids, networks)
  }, [dateString, suppressedMode, networkScope, heylinkUuid, customNetworkUuids, networks])
  const summary = useApiGet<IAnomaliesV2Summary>(
    '/anomalies/v2/summary',
    summaryParams,
    [dateString, suppressedMode, networkScope, heylinkUuid, JSON.stringify(customNetworkUuids), suppressionRefetchKey]
  )

  const alertsParams = useMemo(() => {
    if (!dateString) return undefined
    const params: Record<string, string | number> = {
      date: dateString,
      page,
      page_size: pageSize,
      suppressed: suppressedMode,
    }
    if (search) params.q = search
    if (device) params.device = device
    if (alertFamily) params.alert_family = alertFamily
    if (primaryMetric) params.primary_metric = primaryMetric
    if (status) params.status = status
    return applyNetworkScopeParams(params, networkScope, heylinkUuid, customNetworkUuids, networks)
  }, [dateString, search, device, alertFamily, primaryMetric, status, page, pageSize, suppressedMode, networkScope, heylinkUuid, customNetworkUuids, networks])

  const alertsData = useApiGet<IAnomaliesV2AlertsResponse>(
    '/anomalies/v2/alerts',
    alertsParams,
    [dateString, search, device, alertFamily, primaryMetric, status, page, pageSize, suppressedMode, networkScope, heylinkUuid, JSON.stringify(customNetworkUuids), suppressionRefetchKey]
  )

  // ----- Saved filter presets -------------------------------------------
  // Same shape as the querystring the data endpoints receive — minus the
  // bits that should always default to "latest" (date) or that are pure
  // pagination/sorting (page, page_size, sort, dir).
  const presetValues = useMemo<UserFilterValues>(() => {
    const v: UserFilterValues = {}
    if (search) v.q = search
    if (device) v.device = device
    if (alertFamily) v.alert_family = alertFamily
    if (primaryMetric) v.primary_metric = primaryMetric
    if (status) v.status = status
    if (suppressedMode && suppressedMode !== 'exclude') v.suppressed = suppressedMode
    // Network scope: persist the pill choice, plus the custom selection if
    // any. The custom UUIDs are stored as a comma string so the backend can
    // round-trip them as a single `values` entry.
    if (networkScope !== 'all') v.network_scope = networkScope
    if (networkScope === 'custom' && customNetworkUuids.length > 0) {
      v.custom_networks = customNetworkUuids.join(',')
    }
    return v
  }, [
    search,
    device,
    alertFamily,
    primaryMetric,
    status,
    suppressedMode,
    networkScope,
    customNetworkUuids,
  ])

  // Apply a preset bag back into local state. Unknown keys are ignored so a
  // preset saved by a future, richer version of this UI degrades gracefully.
  const applyPresetValues = (values: UserFilterValues) => {
    const next: { key: string; label: string; value: string }[] = [
      { key: 'device', label: '', value: String(values.device ?? '') },
      { key: 'alert_family', label: '', value: String(values.alert_family ?? '') },
      { key: 'primary_metric', label: '', value: String(values.primary_metric ?? '') },
      { key: 'status', label: '', value: String(values.status ?? '') },
    ]
    setFilters(next)
    const q = values.q != null ? String(values.q) : ''
    setSearch(q)
    setSearchInput(q)
    const sup = values.suppressed
    if (sup === 'only' || sup === 'include' || sup === 'exclude') {
      setSuppressedMode(sup)
    } else {
      setSuppressedMode('exclude')
    }
    const scope = values.network_scope
    if (scope === 'direct' || scope === 'affiliate' || scope === 'custom') {
      setNetworkScope(scope)
    } else {
      setNetworkScope('all')
    }
    if (values.custom_networks != null) {
      setCustomNetworkUuids(String(values.custom_networks).split(',').filter(Boolean))
    } else {
      setCustomNetworkUuids([])
    }
    setPage(1)
  }

  const presetsState = useUserFilters('triage')

  // First-visit auto-apply: on a clean session (no sessionStorage snapshot)
  // and once the presets list resolves, push the default preset into state.
  // If the operator left mid-task we don't override what they had.
  const autoAppliedRef = React.useRef(hadSnapshotOnMount)
  useEffect(() => {
    if (autoAppliedRef.current) return
    if (!presetsState.presets) return
    autoAppliedRef.current = true
    const def = findDefaultPreset(presetsState.presets)
    if (def) applyPresetValues(def.values)
  }, [presetsState.presets])

  // After alerts arrive on first mount, restore scroll position once.
  const scrollRestored = React.useRef(false)
  useEffect(() => {
    if (scrollRestored.current) return
    if (!alertsData) return
    const y = initialSnap.scrollY
    if (typeof y === 'number' && y > 0) {
      requestAnimationFrame(() => window.scrollTo(0, y))
    }
    scrollRestored.current = true
  }, [alertsData])

  const rawAlerts = alertsData?.alerts || []
  const total = alertsData?.total || 0
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const suppressedCount = alertsData?.suppressedCount ?? 0

  // Client-side sort over the loaded page. The backend always returns by
  // priority_score desc, so headers re-order what's already visible.
  // Also strips out rows the operator just optimistically suppressed (only
  // matters in "exclude" mode — in "only" mode the row would still be valid).
  const alerts = useMemo(() => {
    const filtered =
      suppressedMode === 'exclude' && optimisticHidden.size > 0
        ? rawAlerts.filter((a) => !isOptimisticallyHidden(a))
        : rawAlerts
    const list = [...filtered]
    const mul = dir === 'asc' ? 1 : -1
    return list.sort((a, b) => {
      const av = sortValueFor(a, sort)
      const bv = sortValueFor(b, sort)
      if (av === bv) return 0
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * mul
      }
      return String(av).localeCompare(String(bv)) * mul
    })
  }, [rawAlerts, sort, dir, suppressedMode, optimisticHidden])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput.trim())
  }

  // Triage row click: open the unified detail page with full alert context
  // — date / device / metric. Drift gets a 400d default range so the rolling
  // 6m anchor sits comfortably inside the chart; everything else 180d.
  const handleRowClick = (alert: IAnomaliesV2Alert) => (e: React.MouseEvent) => {
    captureScrollBeforeNav()
    const days = alert.alertFamily === 'drift' ? 400 : 180
    const params = new URLSearchParams({
      date: alert.eventDate,
      device: alert.device,
      metric: alert.primaryMetric,
      days: String(days),
    })
    navigateTo(
      e,
      `/anomalies/${encodeURIComponent(alert.advertiserDomain)}?${params.toString()}`
    )
  }

  // Advertiser-link click: open the same page in advertiser-only mode (no
  // date/device/metric) so the backend resolves defaults.
  const handleAdvertiserClick =
    (advertiserDomain: string) => (e: React.MouseEvent) => {
      e.stopPropagation()
      captureScrollBeforeNav()
      navigateTo(
        e,
        `/anomalies/${encodeURIComponent(advertiserDomain)}`
      )
    }

  const handleTimelineDateClick = (clickedDate: string) => {
    try {
      setDate(parseISO(clickedDate))
    } catch {
      // ignore malformed date
    }
  }

  const headProps = { sort, dir, toggleSort }

  const [faqOpen, setFaqOpen] = useState(false)

  return (
    <div className="anomalies-page">
      <div className="row row-center row-space-between">
        <Header label="Anomaly triage" />
        <div className="row row-center" style={{ gap: 12 }}>
          <button
            type="button"
            className="anomalies-faq-toggle"
            onClick={() => setFaqOpen((v) => !v)}
            aria-expanded={faqOpen}
          >
            {faqOpen ? 'Hide FAQ' : 'FAQ'}
          </button>
          <AdvertiserSearch width={200} />
        </div>
      </div>

      {faqOpen && (
        <div className="card m-t-3">
          <div className="card-body">
            <Faq />
          </div>
        </div>
      )}

      <div className="row row-end m-t-4 m-b-2">
        <div className="anomalies-trend-toggle-group">
          {[30, 90, 180, 400].map((d) => (
            <button
              key={d}
              type="button"
              className={varClass({
                'anomalies-trend-toggle': true,
                'active': timelineDays === d,
              })}
              onClick={() => setTimelineDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <TimelineChart
        data={timeline}
        selectedDate={dateString || undefined}
        onSelectDate={handleTimelineDateClick}
      />

      <Loadable data={summary}>
        {summary && (
          <SummaryCards
            summary={summary}
            filters={filters}
            setFilters={setFilters}
            suppressedCount={summary.suppressedAlerts ?? suppressedCount}
            suppressedMode={suppressedMode}
            onShowSuppressedOnly={() => setSuppressedMode('only')}
          />
        )}
      </Loadable>

      {suppressedMode === 'only' && (
        <div className="anomalies-suppressed-only-banner m-t-3">
          <span>
            <b>Showing suppressed only.</b> These alerts are currently
            hidden from the default view.
          </span>
          <button
            type="button"
            className="anomalies-suppressed-banner-back"
            onClick={() => setSuppressedMode('exclude')}
          >
            Back to default
          </button>
        </div>
      )}

      <div className="card m-t-4">
        <div className="p-y-20 m-t--3 m-b-10">
          <div className="row row-space-between row-center p-t-05 m-t-4 m-b-3">
            <div
              className="row row-center"
              style={{ gap: 16, alignItems: 'flex-end' }}
            >
              <PresetsControl
                scope="triage"
                currentValues={presetValues}
                onApply={applyPresetValues}
                presetsState={presetsState}
              />
              <div className="row row-center" style={{ gap: 12 }}>
                <div className="control control-date" style={{ width: 170 }}>
                  <DateInput
                    selected={date}
                    onChange={(newDate: Date | null) => setDate(newDate)}
                    blankValue="Pick date"
                  />
                </div>
                <form onSubmit={handleSearchSubmit}>
                  <div className="control" style={{ width: 220 }}>
                    <input
                      type="text"
                      placeholder="Search advertiser…"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onBlur={() => setSearch(searchInput.trim())}
                    />
                  </div>
                </form>
              </div>
            </div>
            <FilterGroup label="Networks">
              <NetworkScopePill
                value={networkScope}
                onChange={setNetworkScope}
                customUuids={customNetworkUuids}
                onCustomUuidsChange={setCustomNetworkUuids}
              />
            </FilterGroup>
          </div>

          <div className="row m-t-2 m-b-4">
            <FilterGroup label="Filters" grow>
            <NavFilter
              filters={filters}
              setFilters={setFilters}
              filterLabels={{
                device: 'Device',
                alert_family: 'Family',
                primary_metric: 'Metric',
                status: 'Status',
              }}
              filterOptions={{
                device: DEVICE_OPTIONS,
                alert_family: FAMILY_OPTIONS,
                primary_metric: METRIC_OPTIONS,
                status: STATUS_OPTIONS,
              }}
              hideAdd
              hideRemove
              hideChecks={false}
            />
            </FilterGroup>
          </div>
          <div className="anomalies-status-legend">
            <b>NEW</b> first day this alert fired
            <span className="anomalies-status-legend-sep">·</span>
            <b>WORSENING</b> score increased vs yesterday
            <span className="anomalies-status-legend-sep">·</span>
            <b>CONTINUING</b> fired again, score unchanged or down
          </div>
        </div>

        <Loadable data={alertsData}>
          <div className="table-container-overflow m-x--6">
            <table className="table table-bordered table-sortable table-hoverable anomalies-triage-table">
              <thead className="text-nowrap">
                <tr>
                  <TableHead value="rank" label="#" {...headProps} />
                  <TableHead value="status" label="Status" {...headProps} />
                  <TableHead value="advertiser_domain" label="Advertiser" {...headProps} />
                  <TableHead value="device" label="Device" {...headProps} />
                  <TableHead value="issue" label="Issue" {...headProps} />
                  <TableHead
                    value="current_value"
                    label="Current"
                    desc
                    className="text-right"
                    {...headProps}
                  />
                  <TableHead
                    value="baseline_value"
                    label="Baseline"
                    desc
                    className="text-right"
                    {...headProps}
                  />
                  <TableHead
                    value="percent_change"
                    label="Change"
                    desc
                    className="text-right"
                    {...headProps}
                  />
                  <TableHead value="impact" label="Impact" desc {...headProps} />
                  <TableHead
                    value="priority_score"
                    label="Score"
                    desc
                    className="text-right"
                    {...headProps}
                  />
                  <TableHead
                    value="alert_first_seen_date"
                    label="First seen"
                    {...headProps}
                  />
                  <th className="text-right" />
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <AlertRow
                    key={`${a.advertiserDomain}_${a.device}_${a.rank}`}
                    alert={a}
                    onClick={handleRowClick(a)}
                    onAdvertiserClick={handleAdvertiserClick(a.advertiserDomain)}
                    onSuppress={(scope) =>
                      openSuppressModal(a.advertiserDomain, scope)
                    }
                  />
                ))}
                {alerts.length === 0 && (
                  <tr>
                    <td
                      colSpan={12}
                      className="text-center text-light p-y-10"
                    >
                      No alerts for {dateString || 'this date'}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="row row-space-between row-center m-t-3">
              <div className="text-light text-small">
                {total} result{total === 1 ? '' : 's'}
              </div>
              <div className="row row-center" style={{ gap: 12 }}>
                <Pagination page={page} setPage={setPage} maxPage={totalPages} />
                <label
                  className="text-light text-small row row-center"
                  style={{ gap: 6 }}
                >
                  Page size
                  <select
                    className="anomalies-page-size-select"
                    value={pageSize}
                    onChange={(e) =>
                      setPageSize(parseInt(e.target.value, 10))
                    }
                  >
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}
        </Loadable>
      </div>
    </div>
  )
}

type FilterEntry = { key: string; label: string; value: string }

// A card click sets one or more filter values. "Active alerts" clears every
// filter (the unfiltered view); the family/severity cards each set just one.
type FilterPatch = Partial<Record<'status' | 'alert_family', string>>

const applyFilterPatch = (
  filters: FilterEntry[],
  patch: FilterPatch | null
): FilterEntry[] => {
  return filters.map((f) => {
    if (patch === null) return { ...f, value: '' }
    if (Object.prototype.hasOwnProperty.call(patch, f.key)) {
      return { ...f, value: patch[f.key as keyof FilterPatch] || '' }
    }
    // Other filters are left alone so an "Active alerts" reset doesn't blow
    // away device/metric choices the user already made.
    return f
  })
}

const SummaryCards = (props: {
  summary: IAnomaliesV2Summary
  filters: FilterEntry[]
  setFilters: (next: FilterEntry[]) => void
  suppressedCount: number
  suppressedMode: TAnomaliesSuppressedMode
  onShowSuppressedOnly: () => void
}) => {
  const {
    summary,
    filters,
    setFilters,
    suppressedCount,
    suppressedMode,
    onShowSuppressedOnly,
  } = props
  const status = filters.find((f) => f.key === 'status')?.value || ''
  const family = filters.find((f) => f.key === 'alert_family')?.value || ''

  const apply = (patch: FilterPatch | null) => () =>
    setFilters(applyFilterPatch(filters, patch))

  // "Active alerts" is the un-narrowed view: family + status filters cleared.
  const noStatusOrFamily = !status && !family

  // Build the "+N suppressed" pill displayed inside the Active card. Clicking
  // it switches the page into "only" mode so the operator can audit what's
  // currently hidden. Suppressed in "only" mode shows "X visible (suppressed)".
  const suppressedBadge =
    suppressedCount > 0 && suppressedMode !== 'only' ? (
      <button
        type="button"
        className="anomalies-suppressed-badge"
        onClick={(e) => {
          e.stopPropagation()
          onShowSuppressedOnly()
        }}
        title="Show suppressed alerts"
      >
        +{formatInteger(suppressedCount)} suppressed
      </button>
    ) : null

  return (
    <div className="anomalies-summary-grid m-t-2">
      <SummaryCard
        label="Active alerts"
        value={formatInteger(summary.activeAlerts)}
        info="Total advertiser×device pairs with at least one alert today."
        active={noStatusOrFamily}
        onClick={apply({ status: '', alert_family: '' })}
        extra={suppressedBadge}
      />
      <SummaryCard
        label="New"
        value={formatInteger(summary.newAlerts)}
        accent="#5DC97E"
        info="Alerts firing today that did NOT fire yesterday."
        active={status === 'new'}
        onClick={apply({ status: 'new', alert_family: '' })}
      />
      <SummaryCard
        label="Worsening"
        value={formatInteger(summary.worseningAlerts)}
        accent="#EBB617"
        info="Active alerts whose priority score increased today vs yesterday."
        active={status === 'worsening'}
        onClick={apply({ status: 'worsening', alert_family: '' })}
      />
      <SummaryCard
        label="Drift"
        value={formatInteger(summary.families?.drift)}
        accent={FAMILY_COLORS.drift}
        info="Long-term decline vs 6-month historical anchor."
        active={family === 'drift'}
        onClick={apply({ alert_family: 'drift', status: '' })}
      />
      <SummaryCard
        label="Acute"
        value={formatInteger(summary.families?.acute)}
        accent={FAMILY_COLORS.acute}
        info="Sharp recent drop vs 30d/90d baseline (z-score ≤ −2.5)."
        active={family === 'acute'}
        onClick={apply({ alert_family: 'acute', status: '' })}
      />
    </div>
  )
}

const SummaryCard = (props: {
  label: string
  value: string
  accent?: string
  info?: string
  active?: boolean
  onClick?: () => void
  extra?: React.ReactNode
}) => (
  <button
    type="button"
    className={varClass({
      'card': true,
      'anomalies-summary-card': true,
      'anomalies-summary-card-active': !!props.active,
    })}
    style={
      props.accent ? { borderTop: `3px solid ${props.accent}` } : undefined
    }
    onClick={props.onClick}
  >
    <div className="card-body p-y-2">
      <div className="text-light text-small">{props.label}</div>
      <div className="row row-center" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="text-dark text-bigger text-bolder">{props.value}</span>
        {props.extra}
      </div>
      {props.info && (
        <div className="anomalies-summary-card-hint">{props.info}</div>
      )}
    </div>
  </button>
)

// The row's metric as a suppression scope, when the backend supports scoping
// to it. Daily rejection alerts carry primaryMetric 'rejection_rate' which is
// NOT a suppression scope ('rejections' targets the monthly review instead),
// so those rows only offer the all-alerts action.
const suppressibleMetricFor = (
  alert: IAnomaliesV2Alert
): Exclude<TAnomaliesSuppressionScope, 'rejections'> | null =>
  alert.primaryMetric in SUPPRESSION_METRIC_LABELS
    ? (alert.primaryMetric as Exclude<TAnomaliesSuppressionScope, 'rejections'>)
    : null

const AlertRow = (props: {
  alert: IAnomaliesV2Alert
  onClick: (e: React.MouseEvent) => void
  onAdvertiserClick: (e: React.MouseEvent) => void
  onSuppress: (scope: SuppressScopeChoice) => void
}) => {
  const { alert, onClick, onAdvertiserClick, onSuppress } = props
  const badges = badgesFor(alert)
  const familyColor = FAMILY_COLORS[alert.alertFamily]
  const commissionMissing = isCommissionMissing(alert)
  const isRejection = alert.alertFamily === 'rejection'
  const suppressibleMetric = suppressibleMetricFor(alert)

  // Rejection alerts are volume-shaped (count of reported rejections in the
  // last 7d vs. weekly-equivalent baseline) — so currentValue/baselineValue
  // are now counts, not rates. percentChange = (current - baseline) / baseline,
  // positive = spike. We render counts + thousands separators and switch to
  // a multiplier when the spike is huge.
  const formatMultiplier = (ratio: number): string => {
    if (ratio > 5) return `+${formatDecimal(ratio, 1)}×`
    return `+${Math.round(ratio * 100)}%`
  }

  let currentCell: React.ReactNode
  let currentTitle: string | undefined
  let baselineCell: React.ReactNode
  let baselineTitle: string | undefined
  let changeCell: React.ReactNode
  let changeIsRed: boolean

  if (isRejection) {
    currentCell =
      alert.currentValue == null ? '—' : formatInteger(alert.currentValue)
    currentTitle = 'Rejections reported in last 7 days'
    baselineCell =
      alert.baselineValue == null ? '—' : formatDecimal(alert.baselineValue, 1)
    baselineTitle = 'Typical weekly volume of rejections being reported'
    changeCell =
      alert.percentChange == null
        ? '—'
        : formatMultiplier(alert.percentChange)
    // Positive % = spike = bad → highlight as red. Negative not really
    // expected for a rejection alert but render plainly if it shows up.
    changeIsRed = (alert.percentChange ?? 0) > 0
  } else {
    // Commission-missing rows show 0 DKK explicitly — the whole point of the
    // alert is "commission stopped" so a dash would hide the signal. Other
    // metrics still get the dash for genuinely missing values.
    currentCell =
      commissionMissing && alert.currentValue == null
        ? '0 DKK EPC'
        : commissionMissing
        ? `${formatMetricValue(alert.currentValue, alert.primaryMetric)} EPC`
        : formatMetricValue(alert.currentValue, alert.primaryMetric)
    baselineCell = formatMetricValue(alert.baselineValue, alert.primaryMetric)
    changeCell = formatPercentChange(alert.percentChange)
    changeIsRed =
      alert.percentChange != null && alert.percentChange < 0
  }

  return (
    <tr className="cursor-pointer anomalies-triage-row" onClick={onClick}>
      <td className="text-light">{alert.rank}</td>
      <td>
        <div className="anomalies-badge-row">
          {badges.map((b) => (
            <span
              key={b.label}
              className={`anomalies-status-badge anomalies-status-badge-${b.variant}`}
            >
              {b.label}
            </span>
          ))}
          {badges.length === 0 && (
            <span className="text-light text-small">—</span>
          )}
        </div>
      </td>
      <td>
        <a
          className="text-dark text-bold anomalies-advertiser-link"
          href={`/anomalies/${encodeURIComponent(alert.advertiserDomain)}`}
          onClick={onAdvertiserClick}
        >
          {alert.advertiserDomain}
        </a>
      </td>
      <td>
        <span className="text-light text-small">{alert.device}</span>
      </td>
      <td className="anomalies-triage-cell-compact">
        <div className="row row-center" style={{ gap: 6 }}>
          <span
            className="anomalies-family-pill"
            style={{ background: `${familyColor}1A`, color: familyColor }}
            title={`${FAMILY_LABELS[alert.alertFamily]} family`}
          >
            {FAMILY_LABELS[alert.alertFamily]}
          </span>
          <span className="text-dark text-bold">{displayLabelFor(alert)}</span>
        </div>
        <div className="text-light anomalies-issue-reason">
          {europeanizeNumbers(alert.reasonText)}
        </div>
      </td>
      <td className="text-right text-dark anomalies-triage-cell-compact" title={currentTitle}>
        {currentCell}
      </td>
      <td className="text-right text-light" title={baselineTitle}>
        {baselineCell}
      </td>
      <td
        className={varClass({
          'text-right': true,
          'text-bold': true,
          'text-red': changeIsRed,
        })}
      >
        {changeCell}
      </td>
      <td className="anomalies-triage-cell-compact">
        <CompactImpact alert={alert} />
      </td>
      <td className="text-right text-bold">{formatScore(alert.priorityScore)}</td>
      <td className="text-light anomalies-triage-cell-compact">
        {alert.alertFirstSeenDate
          ? format(parseISO(alert.alertFirstSeenDate), 'yyyy-MM-dd')
          : '—'}
      </td>
      <td className="text-right" onClick={(e) => e.stopPropagation()}>
        <div className="anomalies-row-suppress-group">
          <button
            type="button"
            className="anomalies-row-suppress"
            onClick={() => onSuppress('')}
            title={`Hide all daily alerts for ${alert.advertiserDomain}`}
          >
            Suppress
          </button>
          {suppressibleMetric && (
            <button
              type="button"
              className="anomalies-row-suppress"
              onClick={() => onSuppress(suppressibleMetric)}
              title={`Hide only ${SUPPRESSION_METRIC_LABELS[suppressibleMetric]} alerts for ${alert.advertiserDomain}`}
            >
              {SUPPRESSION_METRIC_LABELS[suppressibleMetric]} only
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

// "Lost: 17 conv." beats "Est. 17 lost conversions" — we already show
// labels in the column header, so the value alone is enough.
const CompactImpact = (props: { alert: IAnomaliesV2Alert }) => {
  const { alert } = props

  // Strip the "Est. " prefix the backend pre-pends. Convert "lost
  // conversions" to "conv." so the label fits. Backend formats numbers
  // en-US (1,234.56) — flip to European (1.234,56) for parity with our
  // own client-side formatters.
  const lostLine = europeanizeNumbers(alert.estimatedLossLabel || '')
    .replace(/^Est\.?\s*/i, '')
    .replace(/\blost\s+/gi, '')
    .replace(/\bconversions?\b/gi, 'conv.')

  return (
    <div>
      {lostLine && (
        <div>
          <span className="text-light">Lost:&nbsp;</span>
          <span className="text-dark">{lostLine}</span>
        </div>
      )}
      {alert.commission30dDkk != null && alert.commission30dDkk > 0 && (
        <div className="text-light">
          {formatDkkLarge(alert.commission30dDkk)} / 30d
        </div>
      )}
      {!lostLine && alert.commission30dDkk == null && (
        <span className="text-light">—</span>
      )}
    </div>
  )
}
